// @flow
import Store from 'store';
import type {SagaIterator} from 'redux-saga';

import {PRODUCT} from 'constants/actions';
import type {ApplicationStoreType} from 'constants/flow/flowTypes';
import {put, call, fork, select, putResolve, all} from 'redux-saga/effects';
import {getProduct, setActiveProductId, resetQuoteItem} from 'actions/product/product';
import {globalDataLayerPageSaga} from 'actions/globalDataLayer/globalDataLayer';
import {fetchProductReviews} from 'sagas/product/reviews/reviews';
import {clearReviews} from 'actions/product/reviews/reviews';
import {displayBanner} from 'sagas/product/banner/banner';
import {
    getActiveChildrenProductId,
    isActiveChildrenProductPreloaded,
} from 'selectors/product/product';
import {fetchProductForQuickCheckout} from 'features/quickCheckout/sagas/product/productData/productData';
import {isQuickCheckoutActivated} from 'libs/quickCheckout/quickCheckout';
import {getCoupon} from 'selectors/wizard/discountPrice';

/**
 * Prefetch product. It uses value from localstorage if it exist
 * @param {ApplicationStoreType} persistentState - value from localstorage
 * @param {number} productId - from router
 */
export function* preFetchProduct(
    persistentState: ApplicationStoreType,
    productId: number,
): SagaIterator {
    if (persistentState.product) {
        yield put({
            type: `${PRODUCT.GET_PRODUCT}_FULFILLED`,
            payload: {data: persistentState.product},
        });
    } else {
        if (isQuickCheckoutActivated()) {
            yield call(fetchProductForQuickCheckout, productId);
            return;
        }

        const promoCode = yield select(getCoupon);
        const getProductAction = yield call(getProduct, productId, promoCode);

        yield putResolve(getProductAction);
    }
}

/**
 * Prefetch product reviews. It uses value from localstorage if it exist
 * @param {ApplicationStoreType} persistentState - value from localstorage
 * @param {number} productId - from router
 */
export function* preFetchReviews(
    persistentState: ApplicationStoreType,
    productId: number,
): SagaIterator {
    if (persistentState.productReviews) {
        yield put({
            type: `${PRODUCT.FETCH_REVIEWS}_FULFILLED`,
            payload: {data: persistentState.productReviews},
        });
    } else {
        yield call(fetchProductReviews, productId);
    }
}

/**
 * Fetching a product by product id
 * @param {number} id - Product id
 */
export function* checkPreloadedData(id: number): SagaIterator {
    const persistentState = yield call([Store, 'get'], 'persistentState', {});
    yield call([Store, 'remove'], 'persistentState');
    yield all([
        call(preFetchProduct, persistentState, id),
        call(preFetchReviews, persistentState, id),
    ]);
}

/**
 * Saves active product identifier
 * @param {number} id - product identifier
 */
export function* saveActiveProductId(id: number): SagaIterator {
    const currentProductId = yield select(getActiveChildrenProductId);
    if (!isQuickCheckoutActivated() || currentProductId !== id) yield put(setActiveProductId(id));
}

/**
 * Checks need for product data fetch
 * @param {number} id - product id that should be fetched
 * @returns {SagaIterator<boolean>} - checking verdict
 */
export function* checkIsProductFetchRequired(id: number): SagaIterator<boolean> {
    const currentProductId = yield select(getActiveChildrenProductId);
    const isPreloaded = yield select(isActiveChildrenProductPreloaded);

    return id !== currentProductId || isPreloaded;
}

/**
 * Fetching a product by product id
 * @param {number} id - Product id
 */
export function* fetchProduct(id: number): SagaIterator {
    const shouldBeFetched = yield call(checkIsProductFetchRequired, id);

    if (shouldBeFetched) {
        yield fork(displayBanner, id);
        yield put(resetQuoteItem());
        yield put(clearReviews());
        yield call(checkPreloadedData, id);
        yield put(globalDataLayerPageSaga('product'));
        yield call(saveActiveProductId, id);
    }
}
